import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import siteConfig from '../data/siteConfig.json';
import SafeIcon from '../common/SafeIcon';
import { 
  FiStar
} from 'react-icons/fi';

const Home = () => {
  const { t } = useLanguage();

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[calc(100svh-72px)] lg:min-h-[680px] flex items-center bg-glass-dark overflow-hidden pt-14 sm:pt-20 lg:pt-24 pb-28 sm:pb-32 lg:pb-36">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={siteConfig.images.homeHero}
            alt="Auto Glass Repair" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-glass-dark via-glass-dark/80 to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-3xl text-white"
          >
            <div className="inline-block px-4 py-1.5 bg-glass-blue/20 border border-glass-blue/30 rounded-full text-glass-blue text-xs md:text-sm font-semibold tracking-wide mb-6">
              {t('trust.specialists')}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.06] mb-5 sm:mb-6 max-w-4xl">
              {t('hero.title').split(' ').map((word, i) => {
                if(word.includes('Dorval') || word.includes('Replacement') || word.includes('Remplacement')) {
                   return <span key={i} className="text-glass-blue">{word} </span>
                }
                return word + ' ';
              })}
            </h1>
            <p className="text-base md:text-xl text-slate-300 mb-8 sm:mb-10 leading-relaxed max-w-xl">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`tel:${siteConfig.business.phoneHref}`} className="bg-glass-blue hover:bg-blue-600 text-white text-center px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg hover:shadow-glass-blue/30 active:scale-95">
                {t('common.callNow')}
              </a>
              <Link to="/contact" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white text-center px-8 py-4 rounded-lg font-bold text-lg transition-all active:scale-95">
                {t('common.getQuote')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Bar - Adjusted for mobile overlap */}
      <section className="relative z-20 -mt-16 sm:-mt-20 px-4">
        <div className="container mx-auto max-w-6xl bg-white shadow-2xl rounded-2xl p-5 sm:p-6 md:p-8 lg:p-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 lg:divide-x lg:divide-slate-100">
            {['local', 'insurance', 'fast', 'specialists'].map((key) => (
              <div key={key} className="px-2 sm:px-4">
                <h3 className="font-bold text-slate-800 text-sm md:text-base leading-tight text-center">{t(`trust.${key}`)}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-28 bg-slate-50">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-10 sm:mb-14 lg:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">{t('services.title')}</h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">{t('services.subtitle')}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {['replacement', 'repair', 'side', 'rear', 'sunroof', 'insurance'].map((serviceKey, index) => (
              <motion.div 
                key={serviceKey}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0, transition: { delay: index * 0.1 } }
                }}
              className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all border border-slate-100 group flex flex-col h-full"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-3">{t(`services.items.${serviceKey}.title`)}</h3>
                <p className="text-slate-600 leading-relaxed mb-8 flex-grow">
                  {t(`services.items.${serviceKey}.desc`)}
                </p>
                <Link to="/services" className="text-glass-blue font-bold">
                  {t('common.readMore')}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Garage Services Preview */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex px-4 py-2 bg-glass-blue/10 text-glass-blue rounded-full text-sm font-bold mb-5">
                {t('garageServices.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5">{t('garageServices.title')}</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">{t('garageServices.subtitle')}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/contact" className="inline-flex items-center justify-center bg-glass-blue hover:bg-blue-600 text-white px-7 py-3.5 rounded-lg font-bold transition-colors">
                  {t('common.bookAppointment')}
                </Link>
                <Link to="/services" className="inline-flex items-center justify-center bg-slate-900 hover:bg-glass-blue text-white px-7 py-3.5 rounded-lg font-bold transition-colors">
                  {t('common.readMore')}
                </Link>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              {[
                {
                  title: t('garageServices.sections.maintenance.title'),
                  items: t('garageServices.sections.maintenance.items')
                },
                {
                  title: t('garageServices.sections.tires.title'),
                  items: [t('garageServices.sections.tires.desc'), t('garageServices.payAtGarage')]
                },
                {
                  title: t('garageServices.sections.hardware.title'),
                  items: t('garageServices.sections.hardware.items').slice(0, 3)
                }
              ].map((group) => (
                <div key={group.title} className="bg-slate-50 border border-slate-100 rounded-2xl p-6 min-w-0">
                  <h3 className="font-bold text-slate-900 mb-4">{group.title}</h3>
                  <ul className="space-y-3">
                    {group.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-glass-blue flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {t('garageServices.trustSignals').map((signal) => (
              <div key={signal} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-glass-blue flex-shrink-0" />
                <p className="text-sm font-semibold text-slate-700 leading-relaxed">{signal}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Section - Fixed spacing */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-28 bg-glass-dark text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-glass-blue/5 hidden lg:block"></div>
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-10 sm:gap-12 lg:gap-20">
            <div className="w-full lg:w-1/2">
              <div className="relative">
                <div className="absolute -inset-4 bg-glass-blue/20 blur-2xl rounded-full"></div>
                <img 
                  src={siteConfig.images.insurance}
                  alt="Insurance Forms" 
                  className="rounded-2xl shadow-2xl relative z-10 w-full object-cover aspect-[4/3] max-h-[420px]"
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">{t('insuranceSection.title')}</h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                {t('insuranceSection.desc')}
              </p>
              <ul className="grid sm:grid-cols-2 gap-4 mb-8 sm:mb-10">
                {[1, 2, 3].map((num) => (
                  <li key={num} className="flex items-center gap-3 text-slate-200">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-glass-blue flex-shrink-0" />
                    <span className="font-medium">{t(`insuranceSection.bullet${num}`)}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="inline-block w-full sm:w-auto text-center bg-glass-blue hover:bg-blue-600 text-white px-10 py-4 rounded-lg font-bold transition-all shadow-lg active:scale-95">
                {t('common.requestQuote')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us & Areas */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-28 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-14 lg:gap-20 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 sm:mb-10">{t('whyUs.title')}</h2>
              <div className="grid gap-4">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <div key={index} className="flex items-center gap-4 bg-slate-50 p-4 sm:p-5 rounded-xl border border-slate-100 hover:border-glass-blue/30 transition-colors group">
                    <span className="h-2 w-2 rounded-full bg-glass-blue flex-shrink-0" />
                    <span className="font-semibold text-slate-800">{t('whyUs.features')[index]}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <div className="bg-slate-50 rounded-3xl p-6 sm:p-8 md:p-12 border border-slate-100 shadow-inner">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">{t('areas.title')}</h2>
                <p className="text-xl text-slate-600 leading-relaxed mb-10">
                  {t('areas.desc')}
                </p>
                
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                  <div className="flex text-yellow-400 mb-4">
                    {[...Array(5)].map((_, i) => <SafeIcon key={i} icon={FiStar} className="fill-current" />)}
                  </div>
                  <p className="text-slate-700 italic mb-4 leading-relaxed">"Excellent service! They replaced my windshield in Dorval while I was at work. Very professional."</p>
                  <p className="text-sm font-black text-slate-900 uppercase tracking-widest text-[10px]">— Marc L.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 lg:py-24 bg-glass-blue text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-400/20 to-transparent"></div>
        <div className="container mx-auto px-6 max-w-4xl relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-6 sm:mb-8 leading-tight">{t('cta.title')}</h2>
          <p className="text-lg md:text-2xl text-blue-50 mb-8 sm:mb-12 font-medium">{t('cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <a href={`tel:${siteConfig.business.phoneHref}`} className="bg-glass-dark hover:bg-slate-900 text-white px-8 sm:px-12 py-4 sm:py-5 rounded-xl font-bold text-lg sm:text-xl transition-all shadow-2xl active:scale-95">
              {t('common.callNow')}
            </a>
            <Link to="/contact" className="bg-white hover:bg-slate-50 text-glass-dark px-8 sm:px-12 py-4 sm:py-5 rounded-xl font-bold text-lg sm:text-xl transition-all shadow-2xl active:scale-95">
              {t('common.getQuote')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
